import React, { useState, useRef, useEffect } from 'react'
import useI18n from '@/hooks/use-i18n'
import Icon from '@/components/Icon/Icon'
import styles from './Modal.module.scss'
import classNames from 'classnames'

export type ModalProps = {
  onClose?: () => void
  children: React.ReactNode
}

const Modal = ({ children, onClose }: ModalProps) => {
  const { i18n } = useI18n()
  const [isVisible, setIsVisible] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Show modal after delay
    const timer = setTimeout(() => {
      setIsVisible(true)
      document.body.classList.add('global-modal-open')
    }, 300)

    return () => {
      document.body.classList.remove('global-modal-open')
      clearTimeout(timer)
    }
  }, [])

  // Enable wheel scrolling on the modal
  useEffect(() => {
    if (!modalRef.current || !isVisible) return

    const handleWheel = (e: WheelEvent) => {
      if (!modalRef.current) return
      e.preventDefault()

      // Manually scroll the modal
      modalRef.current.scrollTop += e.deltaY
    }

    const overlay = modalRef.current
    overlay.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      overlay.removeEventListener('wheel', handleWheel)
    }
  }, [isVisible])

  const handleDismiss = () => {
    setIsVisible(false)
    document.body.classList.remove('global-modal-open')
    onClose?.()
  }
  if (!isVisible) return null

  return (
    <div
      className={styles.overlay}
      onClick={e => e.target === e.currentTarget && handleDismiss()}
    >
      <div
        ref={modalRef}
        className={classNames(styles.modal)}
        onClick={e => e.stopPropagation()}
      >
        <div
          onClick={handleDismiss}
          aria-label={i18n('ariaCloseModal')}
          className={styles.closeButton}
        >
          <Icon name="close" />
        </div>

        {children}
      </div>
    </div>
  )
}

Modal.displayName = 'Modal'

export default Modal
